import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ProfileProvider } from './contexts/ProfileContext';
import { CompanyProvider } from './contexts/CompanyContext';
// Auth Pages
import Signup from './pages/auth/Signup';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AdminLogin from './pages/auth/AdminLogin';
import DentuluCallback from './pages/auth/DentuluCallback';

import PublicRoute from './utils/PublicRoute';

// Public Pages
import Home from './pages/Home';

// Marketplace Pages (Public)
import MarketplaceLanding from './pages/marketplace/MarketplaceLanding';
import MarketplaceCategories from './pages/marketplace/MarketplaceCategories';
import MarketplaceCategoryView from './pages/marketplace/MarketplaceCategoryView';
import AppDetail from './pages/marketplace/AppDetail';
import MyPurchases from './pages/marketplace/MyPurchases';
import DevelopersMain from './pages/marketplace/DevelopersMain';
import DentalProfessionalsmain from './pages/marketplace/DentalProfessionalsmain';
import SearchResults from './pages/marketplace/SearchResults';


// Developer Pages
import Dashboard from './pages/developer/Dashboard';
import APIKeys from './pages/developer/APIKeys';
import MyApps from './pages/developer/MyApps';
import AppSubscribers from './pages/developer/AppSubscribers';
import Webhooks from './pages/developer/Webhooks';
import Marketplace from './pages/developer/Marketplace';
import SubmitApp from './pages/developer/SubmitApp';
import DocsHome from './pages/developer/DocsHome';
import ApiPlayground from './pages/developer/ApiPlayground';
import ApiSecuritySettings from './pages/developer/ApiSecuritySettings';
import Profile from './pages/developer/ProfilePage';
import Billing from './pages/developer/Billing';
import InviteUsers from './pages/developer/InviteUsers';
import Users from './pages/developer/Users';
import UserPrivileges from './pages/developer/UserPrivileges';
import CompleteProfile from './pages/auth/CompleteProfile';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminApps from './pages/admin/AdminApps';
import AdminLogs from './pages/admin/AdminLogs';
import AdminSettings from './pages/admin/AdminSettings';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminFeatureManager from './pages/admin/AdminFeatureManager';
import AdminCategories from './pages/admin/AdminCategories';
import AdminSubcategories from './pages/admin/AdminSubcategories';
import AdminPricing from './pages/admin/AdminPricing';

// Layouts
import DeveloperLayout from './layouts/DeveloperLayout';
import AdminLayout from './layouts/AdminLayout';

// Route Guards
import DevPrivateRoute from './utils/DevPrivateRoute';
import AdminPrivateRoute from './utils/AdminPrivateRoute';

import PurchaseSuccess from './pages/marketplace/PurchaseSuccess';

import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Context
import { UserProvider } from './contexts/UserContext';

export default function App() {
  return (

    <Router>
      <ProfileProvider>
      <CompanyProvider>

        <Routes>
          {/* Public Marketplace Routes - These come FIRST */}
          <Route path="/" element={<MarketplaceLanding />} />
          <Route path="/marketplace" element={<Navigate to="/" replace />} />
          <Route path="/marketplace/categories" element={<MarketplaceCategories />} />
          <Route path="/marketplace/category/:slug" element={<MarketplaceCategoryView />} />
          <Route path="/marketplace/developers" element={<DevelopersMain />} />
          <Route path="/marketplace/dental-professionals" element={<DentalProfessionalsmain />} />
          <Route path="/marketplace/app/:slug" element={<AppDetail />} />
          {/* <Route path="/marketplace/app/:appId" element={<AppDetail />} /> */}

          {/* Developer Portal Entry */}
          <Route path="/portal" element={<Home />} />

          {/* Auth Routes - Wrapped with PublicRoute */}
          <Route path="/signup" element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          } />
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/admin-login" element={
            <PublicRoute>
              <AdminLogin />
            </PublicRoute>
          } />
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } />
          <Route path="/reset-password" element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } />
          <Route path="/auth/dentulu-callback" element={<DentuluCallback />} />

          {/* Documentation (Public) */}
          <Route path="/docs" element={<DocsHome />} />
          <Route path="/api-docs" element={<ApiPlayground />} />

          {/* Protected Developer Routes */}
          <Route path="/dashboard" element={
            <DevPrivateRoute>
              <DeveloperLayout>
                <Dashboard />
              </DeveloperLayout>
            </DevPrivateRoute>
          } />
          <Route path="/dashboard/api-keys" element={
            <DevPrivateRoute>
              <DeveloperLayout>
                <APIKeys />
              </DeveloperLayout>
            </DevPrivateRoute>
          } />
          <Route path="/dashboard/apps" element={
            <DevPrivateRoute>
              <DeveloperLayout>
                <MyApps />
              </DeveloperLayout>
            </DevPrivateRoute>
          } />
          <Route path="/dashboard/apps/:appId/subscribers" element={
            <DevPrivateRoute>
              <DeveloperLayout>
                <AppSubscribers />
              </DeveloperLayout>
            </DevPrivateRoute>
          } />
          <Route path="/dashboard/webhooks" element={
            <DevPrivateRoute>
              <DeveloperLayout>
                <Webhooks />
              </DeveloperLayout>
            </DevPrivateRoute>
          } />
          <Route path="/dashboard/marketplace" element={
            <DevPrivateRoute>
              <DeveloperLayout>
                <Marketplace />
              </DeveloperLayout>
            </DevPrivateRoute>
          } />
          <Route path="/dashboard/submit-app" element={
            <DevPrivateRoute>
              <DeveloperLayout>
                <SubmitApp />
              </DeveloperLayout>
            </DevPrivateRoute>
          } />
          <Route
            path="/dashboard/profile"
            element={
              <DevPrivateRoute>
                <DeveloperLayout>
                  <Profile />
                </DeveloperLayout>
              </DevPrivateRoute>
            }
          />
          <Route
            path="/dashboard/settings/api-security"
            element={
              <DevPrivateRoute>
                <DeveloperLayout>
                  <ApiSecuritySettings />
                </DeveloperLayout>
              </DevPrivateRoute>
            }
          />
          <Route
            path="/dashboard/billing"
            element={
              <DevPrivateRoute>
                <DeveloperLayout>
                  <Billing />
                </DeveloperLayout>
              </DevPrivateRoute>
            }
          />
          <Route
            path="/dashboard/invite-users"
            element={
              <DevPrivateRoute>
                <DeveloperLayout>
                  <InviteUsers />
                </DeveloperLayout>
              </DevPrivateRoute>
            }
          />
          <Route
            path="/dashboard/users"
            element={
              <DevPrivateRoute>
                <DeveloperLayout>
                  <Users />
                </DeveloperLayout>
              </DevPrivateRoute>
            }
          />
          <Route
            path="/dashboard/users/:memberId/privileges"
            element={
              <DevPrivateRoute>
                <DeveloperLayout>
                  <UserPrivileges />
                </DeveloperLayout>
              </DevPrivateRoute>
            }
          />
          <Route path="/complete-profile" element={<CompleteProfile />} />

          {/* Protected Marketplace Routes */}
          <Route path="/marketplace/my-purchases" element={
            <DevPrivateRoute>
              <MyPurchases />
            </DevPrivateRoute>
          } />

          {/* Protected Admin Routes */}
          <Route path="/admin" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </AdminPrivateRoute>
          } />
          <Route path="/admin/users" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminUsers />
              </AdminLayout>
            </AdminPrivateRoute>
          } />
          <Route path="/admin/apps" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminApps />
              </AdminLayout>
            </AdminPrivateRoute>
          } />
          <Route path="/admin/logs" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminLogs />
              </AdminLayout>
            </AdminPrivateRoute>
          } />
          <Route path="/admin/settings" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminSettings />
              </AdminLayout>
            </AdminPrivateRoute>
          } />
          <Route path="/admin/users/:id" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminUserDetail />
              </AdminLayout>
            </AdminPrivateRoute>
          } />
          <Route path="/admin/internal-sync" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminFeatureManager />
              </AdminLayout>
            </AdminPrivateRoute>
          } />
          <Route path="/admin/categories" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminCategories />
              </AdminLayout>
            </AdminPrivateRoute>
          } />
          <Route path="/admin/subcategories" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminSubcategories />
              </AdminLayout>
            </AdminPrivateRoute>
          } />
          <Route path="/admin/pricing" element={
            <AdminPrivateRoute>
              <AdminLayout>
                <AdminPricing />
              </AdminLayout>
            </AdminPrivateRoute>
          } />

          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Catch all - redirect to marketplace */}
          <Route path="*" element={<Navigate to="/" replace />} />

          <Route path="/marketplace/purchase-success" element={<PurchaseSuccess />} />

          <Route path="/dashboard/submit-app" element={<SubmitApp />} />
          <Route path="/dashboard/edit-app/:appId" element={<SubmitApp />} />
          <Route path="/marketplace/search" element={<SearchResults />} />
        </Routes>
      </CompanyProvider>
      </ProfileProvider>
    </Router >
  );
}